import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface AuditEntry {
  id: string;
  admin_id: string;
  action: string;
  entity_type: string;
  entity_id: string;
  details: Record<string, unknown> | null;
  created_at: string;
  admin_name: string;
  admin_email: string;
  company_name: string | null;
}

export function useAuditLog() {
  const query = useQuery({
    queryKey: ["audit-log"],
    staleTime: 60_000,
    queryFn: async (): Promise<AuditEntry[]> => {
      const { data: logs, error: logsErr } = await supabase
        .from("admin_audit_logs")
        .select("id, admin_id, action, entity_type, entity_id, details, created_at")
        .order("created_at", { ascending: false })
        .limit(200);

      if (logsErr) throw logsErr;
      const rows = logs ?? [];
      if (rows.length === 0) return [];

      const adminIds = Array.from(new Set(rows.map((r) => r.admin_id)));
      const companyIds = Array.from(
        new Set(
          rows
            .filter((r) => r.entity_type === "company")
            .map((r) => r.entity_id)
        )
      );

      const [profilesRes, companiesRes] = await Promise.all([
        supabase
          .from("profiles")
          .select("id, first_name, last_name, email")
          .in("id", adminIds.length ? adminIds : ["00000000-0000-0000-0000-000000000000"]),
        supabase
          .from("companies")
          .select("id, name")
          .in("id", companyIds.length ? companyIds : ["00000000-0000-0000-0000-000000000000"]),
      ]);

      if (profilesRes.error) throw profilesRes.error;
      if (companiesRes.error) throw companiesRes.error;

      const profileMap = new Map(
        (profilesRes.data ?? []).map((p) => [p.id, p])
      );
      const companyMap = new Map(
        (companiesRes.data ?? []).map((c) => [c.id, c.name])
      );

      return rows.map((r) => {
        const profile = profileMap.get(r.admin_id);
        const name = profile
          ? `${profile.first_name ?? ""} ${profile.last_name ?? ""}`.trim() ||
            profile.email ||
            "Unknown admin"
          : "Unknown admin";
        return {
          id: r.id,
          admin_id: r.admin_id,
          action: r.action,
          entity_type: r.entity_type,
          entity_id: r.entity_id,
          details: (r.details as Record<string, unknown> | null) ?? null,
          created_at: r.created_at,
          admin_name: name,
          admin_email: profile?.email ?? "",
          company_name:
            r.entity_type === "company" ? companyMap.get(r.entity_id) ?? null : null,
        };
      });
    },
  });

  return {
    entries: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error ? (query.error as Error).message : null,
    refetch: query.refetch,
  };
}

const getStr = (obj: Record<string, unknown> | null, key: string): string | null => {
  if (!obj) return null;
  const v = obj[key];
  return typeof v === "string" && v.trim() ? v : null;
};

const getNum = (obj: Record<string, unknown> | null, key: string): number | null => {
  if (!obj) return null;
  const v = obj[key];
  return typeof v === "number" ? v : null;
};

export function formatAuditEntry(entry: AuditEntry): string {
  const admin = entry.admin_name;
  const target = entry.company_name ?? entry.entity_id;
  const d = entry.details;

  switch (entry.action) {
    case "suspend_company": {
      const reason = getStr(d, "suspension_reason");
      const count = getNum(d, "suspended_count");
      let s = `${admin} suspended ${target}`;
      if (reason) s += ` — Reason: ${reason}`;
      if (count !== null) s += ` (${count} members affected)`;
      return s;
    }
    case "unsuspend_company": {
      const count = getNum(d, "restored_count");
      return `${admin} reactivated ${target}${
        count !== null ? ` — ${count} members restored` : ""
      }`;
    }
    case "approve_company_reactivation_request": {
      const notes = getStr(d, "notes");
      return `${admin} approved reactivation request for ${target}${
        notes ? ` — Notes: ${notes}` : ""
      }`;
    }
    case "reject_company_reactivation_request": {
      const notes = getStr(d, "notes");
      return `${admin} rejected reactivation request for ${target}${
        notes ? ` — Notes: ${notes}` : ""
      }`;
    }
    case "impersonate_user":
      return `${admin} impersonated a user`;
    default:
      return `${admin} performed ${entry.action} on ${entry.entity_type}`;
  }
}

export function getActionDotColor(action: string): string {
  switch (action) {
    case "suspend_company":
      return "bg-amber-500";
    case "unsuspend_company":
    case "approve_company_reactivation_request":
      return "bg-emerald-500";
    case "reject_company_reactivation_request":
      return "bg-red-500";
    case "impersonate_user":
      return "bg-purple-500";
    default:
      return "bg-slate-500";
  }
}
